import React, { FC } from 'react';
import {
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  Typography,
  Tooltip,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { usePerformance } from '../performance.context';
import { PerformanceReviewType } from '../performance.interfaces';
import {
  REVIEW_TYPE_OPTIONS,
  STATUS_OPTIONS,
  RATING_OPTIONS,
  getReviewTypeIcon,
} from '../performance.helpers';
import { useTranslation } from 'src/hooks/useTranslationHook';

interface PerformanceFiltersProps {
  onReviewTypeChange: (type: PerformanceReviewType) => void;
  onFilterChange: (filterKey: string, value: any) => void;
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onResetFilters: () => void;
}

const PerformanceFilters: FC<PerformanceFiltersProps> = ({
  onReviewTypeChange,
  onFilterChange,
  onSearchChange,
  onResetFilters,
}) => {
  const { state, setSearchQuery } = usePerformance();
  const { translateText } = useTranslation();

  return (
    <Grid item xs={12} sx={{ px: { xs: 1, sm: 3 }, mb: { xs: 2, sm: 3 } }} data-testid="performance-filters">
      <Card elevation={1}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="center">
            {/* Review Type Selection */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: { xs: '16px', sm: '14px' } }}>
                  {translateText('analytics.performance.reviewType')}
                </InputLabel>
                <Select
                  value={state.currentReviewType}
                  label={translateText('analytics.performance.reviewType')}
                  onChange={(e) =>
                    onReviewTypeChange(e.target.value as PerformanceReviewType)
                  }
                  sx={{
                    minHeight: { xs: '44px', sm: 'auto' },
                    fontSize: { xs: '16px', sm: '14px' },
                  }}
                >
                  {REVIEW_TYPE_OPTIONS.map((option) => {
                    const translationKey =
                      option.value === PerformanceReviewType.EXTERNAL
                        ? 'analytics.performance.options.externalPartners'
                        : `analytics.performance.options.${option.value}`;
                    return (
                      <MenuItem 
                        key={option.value} 
                        value={option.value}
                        sx={{ minHeight: { xs: '44px', sm: 'auto' } }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <span style={{ marginRight: 8 }}>
                            {getReviewTypeIcon(option.value)}
                          </span>
                          {translateText(translationKey)}
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            {/* Search */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder={translateText(
                  'analytics.performance.searchReviews'
                )}
                value={state.filters.searchQuery}
                onChange={onSearchChange}
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '16px', sm: '14px' },
                    minHeight: { xs: '44px', sm: 'auto' },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <Search sx={{ mr: 1, color: 'text.secondary' }} />
                  ),
                }}
              />
            </Grid>

            {/* Status Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: { xs: '16px', sm: '14px' } }}>
                  {translateText('analytics.performance.status')}
                </InputLabel>
                <Select
                  value={state.filters.status}
                  label={translateText('analytics.performance.status')}
                  onChange={(e) => onFilterChange('status', e.target.value)}
                  sx={{
                    minHeight: { xs: '44px', sm: 'auto' },
                    fontSize: { xs: '16px', sm: '14px' },
                  }}
                >
                  {STATUS_OPTIONS.map((option) => {
                    let translationKey = '';
                    if (option.value === 'all') {
                      translationKey =
                        'analytics.performance.options.allStatus';
                    } else {
                      const statusKey = option.value
                        .toLowerCase()
                        .replace('_', '');
                      translationKey = `analytics.performance.options.${statusKey}`;
                    }
                    return (
                      <MenuItem 
                        key={option.value} 
                        value={option.value}
                        sx={{ minHeight: { xs: '44px', sm: 'auto' } }}
                      >
                        {translateText(translationKey)}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            {/* Rating Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: { xs: '16px', sm: '14px' } }}>
                  {translateText('analytics.performance.rating')}
                </InputLabel>
                <Select
                  value={state.filters.rating}
                  label={translateText('analytics.performance.rating')}
                  onChange={(e) => onFilterChange('rating', e.target.value)}
                  sx={{
                    minHeight: { xs: '44px', sm: 'auto' },
                    fontSize: { xs: '16px', sm: '14px' },
                  }}
                >
                  {RATING_OPTIONS.map((option) => {
                    let translationKey = '';
                    if (option.value === 'all') {
                      translationKey =
                        'analytics.performance.options.allRatings';
                    } else {
                      const ratingKey = option.value
                        .toLowerCase()
                        .replace('_', '');
                      translationKey = `analytics.performance.options.${ratingKey}`;
                    }
                    return (
                      <MenuItem 
                        key={option.value} 
                        value={option.value}
                        sx={{ minHeight: { xs: '44px', sm: 'auto' } }}
                      >
                        {translateText(translationKey)}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            {/* Filter Actions */}
            <Grid item xs={12} sm={12} md={2}>
              <Stack direction="row" spacing={1}>
                <Tooltip
                  title={translateText('analytics.performance.resetFilters')}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={onResetFilters}
                    disabled={state.isLoading}
                    fullWidth={true}
                    sx={{
                      minHeight: { xs: '44px', sm: '36px' },
                      fontSize: { xs: '16px', sm: '14px' },
                    }}
                  >
                    {translateText('analytics.performance.reset')}
                  </Button>
                </Tooltip>
              </Stack>
            </Grid>
          </Grid>

          {/* Active Filters Display */}
          {(state.filters.status !== 'all' ||
            state.filters.rating !== 'all' ||
            state.filters.searchQuery) && (
            <Box sx={{ mt: { xs: 1.5, sm: 2 } }}>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mb: 1,
                  fontSize: { xs: '14px', sm: '0.875rem' },
                }}
              >
                {translateText('analytics.performance.activeFilters')}:
              </Typography>
              <Stack 
                direction="row" 
                spacing={1} 
                flexWrap="wrap"
                sx={{ gap: { xs: 0.5, sm: 1 } }}
              >
                {state.filters.status !== 'all' &&
                  (() => {
                    const statusKey = state.filters.status
                      .toLowerCase()
                      .replace('_', '');
                    return (
                      <Chip
                        key="status"
                        label={`${translateText('analytics.performance.status')}: ${translateText(`analytics.performance.options.${statusKey}`)}`}
                        size="small"
                        onDelete={() => onFilterChange('status', 'all')}
                      />
                    );
                  })()}
                {state.filters.rating !== 'all' &&
                  (() => {
                    const ratingKey = state.filters.rating
                      .toLowerCase()
                      .replace('_', '');
                    return (
                      <Chip
                        key="rating"
                        label={`${translateText('analytics.performance.rating')}: ${translateText(`analytics.performance.options.${ratingKey}`)}`}
                        size="small"
                        onDelete={() => onFilterChange('rating', 'all')}
                      />
                    );
                  })()}
                {state.filters.searchQuery && (
                  <Chip
                    label={`${translateText('common.search')}: "${state.filters.searchQuery}"`}
                    size="small"
                    onDelete={() => setSearchQuery('')}
                  />
                )}
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>
    </Grid>
  );
};

export default PerformanceFilters;
